// ProfileAnalytics Component
// Shows comprehensive statistics for player profile

import React, { useState, useEffect } from 'react';
import { 
  FaUsers, FaTrophy, FaCalendarCheck, FaChartPie, FaRunning,
  FaCalendarAlt, FaTimes, FaCheckCircle, FaTimesCircle, FaClock,
  FaMedal, FaStar
} from 'react-icons/fa';
import styles from './ProfileAnalytics.module.css';
import playerProfileService from '../../services/playerProfile/playerProfileService';
import LoadingSpinner from '../ui/LoadingSpinner';

const ProfileAnalytics = ({ userId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('clubs');
  
  // Data states
  const [clubHistory, setClubHistory] = useState(null);
  const [eventStats, setEventStats] = useState(null);
  const [awards, setAwards] = useState(null);

  useEffect(() => {
    fetchAllAnalytics();
  }, [userId]);

  const fetchAllAnalytics = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const [clubData, eventData, awardData] = await Promise.all([
        playerProfileService.getClubHistory(userId, { includeRsvpStats: false }),
        playerProfileService.getEventStats(userId),
        playerProfileService.getAggregatedAwards(userId)
      ]);

      setClubHistory(clubData);
      setEventStats(eventData);
      setAwards(awardData);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  // Helper functions
  const calculateAverageProgramDuration = () => {
    if (!clubHistory?.clubs) return 0;
    
    let totalMonths = 0;
    let completedPrograms = 0;

    clubHistory.clubs.forEach(club => {
      club.programs?.forEach(program => {
        if (program.membership.completedAt) {
          const start = new Date(program.membership.enrolledAt);
          const end = new Date(program.membership.completedAt);
          const months = Math.round((end - start) / (1000 * 60 * 60 * 24 * 30));
          totalMonths += months;
          completedPrograms++;
        }
      });
    });

    return completedPrograms > 0 ? Math.round(totalMonths / completedPrograms) : 0;
  };

  const getMostActiveEventPeriod = () => {
    // Simplified - would need event dates from backend
    return 'Q4 2024'; // Placeholder
  };

  const getAttendanceColor = (rate) => {
    if (rate >= 90) return 'var(--accent)';
    if (rate >= 70) return '#FFA500';
    return '#FF6B6B';
  };

  if (loading) {
    return (
      <div className={styles.modal}>
        <div className={styles.modalContent}>
          <div className={styles.loadingContainer}>
            <LoadingSpinner size="large" />
            <p>Loading analytics...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.modal}>
        <div className={styles.modalContent}>
          <button className={styles.closeButton} onClick={onClose}>
            <FaTimes />
          </button>
          <div className={styles.errorContainer}>
            <p>{error}</p>
            <button className={styles.retryButton} onClick={fetchAllAnalytics}>
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const attendancePercentage = eventStats?.attendanceRate 
    ? Math.round(eventStats.attendanceRate * 100) 
    : 0;

  return (
    <div className={styles.modal} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <h2 className={styles.title}>
            <FaChartPie /> Profile Analytics
          </h2>
          <button className={styles.closeButton} onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        {/* Tabs */}
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === 'clubs' ? styles.active : ''}`}
            onClick={() => setActiveTab('clubs')}
          >
            <FaUsers /> Club & Events
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'awards' ? styles.active : ''}`}
            onClick={() => setActiveTab('awards')}
          >
            <FaTrophy /> Awards
          </button>
        </div>

        {/* Content */}
        <div className={styles.content}>
          {/* CLUB & EVENT PARTICIPATION TAB */}
          {activeTab === 'clubs' && (
            <>
              {/* Club History Stats */}
              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>
                  <FaUsers /> Club Membership Overview
                </h3>
                <div className={styles.statsGrid}>
                  <div className={styles.statCard}>
                    <div className={styles.statIcon}>
                      <FaUsers />
                    </div>
                    <div className={styles.statContent}>
                      <div className={styles.statNumber}>
                        {clubHistory?.summary?.totalClubs || 0}
                      </div>
                      <div className={styles.statLabel}>Total Clubs</div>
                      <div className={styles.statDetail}>
                        {clubHistory?.summary?.currentClubs || 0} current
                      </div>
                    </div>
                  </div>

                  <div className={styles.statCard}>
                    <div className={styles.statIcon}>
                      <FaTrophy />
                    </div>
                    <div className={styles.statContent}>
                      <div className={styles.statNumber}>
                        {clubHistory?.summary?.totalPrograms || 0}
                      </div>
                      <div className={styles.statLabel}>Programs Enrolled</div>
                      <div className={styles.statDetail}>
                        {clubHistory?.summary?.activePrograms || 0} active
                      </div>
                    </div>
                  </div>

                  <div className={styles.statCard}>
                    <div className={styles.statIcon}>
                      <FaCalendarAlt />
                    </div>
                    <div className={styles.statContent}>
                      <div className={styles.statNumber}>
                        {calculateAverageProgramDuration()}
                      </div>
                      <div className={styles.statLabel}>Avg Program Duration</div>
                      <div className={styles.statDetail}>months</div>
                    </div>
                  </div>

                  <div className={styles.statCard}>
                    <div className={styles.statIcon}>
                      <FaCheckCircle />
                    </div>
                    <div className={styles.statContent}>
                      <div className={styles.statNumber}>
                        {(clubHistory?.summary?.totalPrograms || 0) - (clubHistory?.summary?.activePrograms || 0)}
                      </div>
                      <div className={styles.statLabel}>Completed Programs</div>
                      <div className={styles.statDetail}>finished</div>
                    </div>
                  </div>
                </div>
              </section>

              {/* Event Participation Stats */}
              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>
                  <FaCalendarCheck /> Event Participation
                </h3>
                
                {/* Overview Cards */}
                <div className={styles.statsGrid}>
                  <div className={styles.statCard}>
                    <div className={styles.statIcon}>
                      <FaCalendarCheck />
                    </div>
                    <div className={styles.statContent}>
                      <div className={styles.statNumber}>{eventStats?.totalRSVPs || 0}</div>
                      <div className={styles.statLabel}>Total RSVPs</div>
                    </div>
                  </div>

                  <div className={styles.statCard}>
                    <div className={styles.statIcon} style={{ color: 'var(--accent)' }}>
                      <FaRunning />
                    </div>
                    <div className={styles.statContent}>
                      <div className={styles.statNumber}>{eventStats?.eventsAttended || 0}</div>
                      <div className={styles.statLabel}>Events Attended</div>
                    </div>
                  </div>

                  <div className={styles.statCard}>
                    <div 
                      className={styles.statIcon}
                      style={{ color: getAttendanceColor(attendancePercentage) }}
                    >
                      <FaChartPie />
                    </div>
                    <div className={styles.statContent}>
                      <div 
                        className={styles.statNumber}
                        style={{ color: getAttendanceColor(attendancePercentage) }}
                      >
                        {attendancePercentage}%
                      </div>
                      <div className={styles.statLabel}>Attendance Rate</div>
                    </div>
                  </div>

                  <div className={styles.statCard}>
                    <div className={styles.statIcon}>
                      <FaCalendarAlt />
                    </div>
                    <div className={styles.statContent}>
                      <div className={styles.statNumber}>{eventStats?.upcomingEvents || 0}</div>
                      <div className={styles.statLabel}>Upcoming Events</div>
                    </div>
                  </div>
                </div>

                {/* RSVP Breakdown */}
                {eventStats?.responseBreakdown && eventStats.totalRSVPs > 0 && (
                  <div className={styles.breakdownSection}>
                    <h4 className={styles.subsectionTitle}>RSVP Response Breakdown</h4>
                    <div className={styles.responseGrid}>
                      <div className={styles.responseCard}>
                        <FaCheckCircle className={styles.responseIcon} style={{ color: 'var(--accent)' }} />
                        <div className={styles.responseContent}>
                          <div className={styles.responseNumber}>{eventStats.responseBreakdown.going || 0}</div>
                          <div className={styles.responseLabel}>Going</div>
                        </div>
                        <div 
                          className={styles.responseBar}
                          style={{ 
                            width: `${((eventStats.responseBreakdown.going || 0) / eventStats.totalRSVPs) * 100}%`,
                            backgroundColor: 'var(--accent)'
                          }}
                        />
                      </div>

                      <div className={styles.responseCard}>
                        <FaClock className={styles.responseIcon} style={{ color: '#FFA500' }} />
                        <div className={styles.responseContent}>
                          <div className={styles.responseNumber}>{eventStats.responseBreakdown.maybe || 0}</div>
                          <div className={styles.responseLabel}>Maybe</div>
                        </div>
                        <div 
                          className={styles.responseBar}
                          style={{ 
                            width: `${((eventStats.responseBreakdown.maybe || 0) / eventStats.totalRSVPs) * 100}%`,
                            backgroundColor: '#FFA500'
                          }}
                        />
                      </div>

                      <div className={styles.responseCard}>
                        <FaTimesCircle className={styles.responseIcon} style={{ color: '#FF6B6B' }} />
                        <div className={styles.responseContent}>
                          <div className={styles.responseNumber}>{eventStats.responseBreakdown.notGoing || 0}</div>
                          <div className={styles.responseLabel}>Not Going</div>
                        </div>
                        <div 
                          className={styles.responseBar}
                          style={{ 
                            width: `${((eventStats.responseBreakdown.notGoing || 0) / eventStats.totalRSVPs) * 100}%`,
                            backgroundColor: '#FF6B6B'
                          }}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* No RSVPs Message */}
                {eventStats && eventStats.totalRSVPs === 0 && (
                  <div className={styles.emptySection}>
                    <FaCalendarCheck className={styles.emptySectionIcon} />
                    <p className={styles.emptySectionText}>No event RSVPs recorded yet</p>
                  </div>
                )}

                {/* Attendance Breakdown */}
                {eventStats?.attendanceBreakdown && (
                  <div className={styles.breakdownSection}>
                    <h4 className={styles.subsectionTitle}>Attendance Details</h4>
                    <div className={styles.attendanceGrid}>
                      <div className={styles.attendanceCard}>
                        <div className={styles.attendanceIcon} style={{ color: 'var(--accent)' }}>✓</div>
                        <div className={styles.attendanceNumber}>{eventStats.attendanceBreakdown.present}</div>
                        <div className={styles.attendanceLabel}>Present</div>
                      </div>

                      <div className={styles.attendanceCard}>
                        <div className={styles.attendanceIcon} style={{ color: '#FFA500' }}>⏰</div>
                        <div className={styles.attendanceNumber}>{eventStats.attendanceBreakdown.late}</div>
                        <div className={styles.attendanceLabel}>Late</div>
                      </div>

                      <div className={styles.attendanceCard}>
                        <div className={styles.attendanceIcon} style={{ color: '#6496FF' }}>📝</div>
                        <div className={styles.attendanceNumber}>{eventStats.attendanceBreakdown.excused}</div>
                        <div className={styles.attendanceLabel}>Excused</div>
                      </div>

                      <div className={styles.attendanceCard}>
                        <div className={styles.attendanceIcon} style={{ color: '#FF6B6B' }}>✗</div>
                        <div className={styles.attendanceNumber}>{eventStats.attendanceBreakdown.absent}</div>
                        <div className={styles.attendanceLabel}>Absent</div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Event Types */}
                {eventStats?.eventTypes && Object.keys(eventStats.eventTypes).length > 0 && (
                  <div className={styles.breakdownSection}>
                    <h4 className={styles.subsectionTitle}>Participation by Event Type</h4>
                    <div className={styles.eventTypesList}>
                      {Object.entries(eventStats.eventTypes).map(([type, count]) => (
                        <div key={type} className={styles.eventTypeItem}>
                          <span className={styles.eventTypeName}>{type}</span>
                          <div className={styles.eventTypeBarContainer}>
                            <div 
                              className={styles.eventTypeBar}
                              style={{ 
                                width: `${(count / Math.max(...Object.values(eventStats.eventTypes))) * 100}%` 
                              }}
                            />
                          </div>
                          <span className={styles.eventTypeCount}>{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </section>
            </>
          )}

          {/* AWARDS & ACHIEVEMENTS TAB */}
          {activeTab === 'awards' && (
            <>
              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>
                  <FaTrophy /> Awards Overview
                </h3>
                
                {/* Total Awards */}
                <div className={styles.highlightCard}>
                  <div className={styles.highlightIcon}>
                    <FaMedal />
                  </div>
                  <div className={styles.highlightContent}>
                    <div className={styles.highlightNumber}>{awards?.total || 0}</div>
                    <div className={styles.highlightLabel}>Total Awards Received</div>
                  </div>
                </div>

                {/* Awards by Type */}
                {awards?.byType && Object.keys(awards.byType).length > 0 && (
                  <div className={styles.breakdownSection}>
                    <h4 className={styles.subsectionTitle}>Awards by Type</h4>
                    <div className={styles.awardTypeGrid}>
                      {Object.entries(awards.byType).map(([type, count]) => (
                        <div key={type} className={styles.awardTypeCard}>
                          <div className={styles.awardTypeIcon}>
                            <FaTrophy />
                          </div>
                          <div className={styles.awardTypeNumber}>{count}</div>
                          <div className={styles.awardTypeLabel}>
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Awards by Sport */}
                {awards?.bySport && Object.keys(awards.bySport).length > 0 && (
                  <div className={styles.breakdownSection}>
                    <h4 className={styles.subsectionTitle}>Awards by Sport</h4>
                    <div className={styles.sportAwardsList}>
                      {Object.entries(awards.bySport)
                        .sort(([, a], [, b]) => b - a)
                        .map(([sport, count]) => (
                          <div key={sport} className={styles.sportAwardItem}>
                            <span className={styles.sportName}>{sport}</span>
                            <div className={styles.sportBarContainer}>
                              <div 
                                className={styles.sportBar}
                                style={{ 
                                  width: `${(count / Math.max(...Object.values(awards.bySport))) * 100}%` 
                                }}
                              />
                            </div>
                            <span className={styles.sportCount}>{count}</span>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Awards by Year */}
                {awards?.byYear && Object.keys(awards.byYear).length > 0 && (
                  <div className={styles.breakdownSection}>
                    <h4 className={styles.subsectionTitle}>Awards Timeline</h4>
                    <div className={styles.yearAwardsChart}>
                      {Object.entries(awards.byYear)
                        .sort(([a], [b]) => b - a)
                        .map(([year, count]) => (
                          <div key={year} className={styles.yearAwardBar}>
                            <span className={styles.yearLabel}>{year}</span>
                            <div className={styles.yearBarContainer}>
                              <div 
                                className={styles.yearBar}
                                style={{ 
                                  width: `${(count / Math.max(...Object.values(awards.byYear))) * 100}%` 
                                }}
                              >
                                <span className={styles.yearBarLabel}>{count}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}

                {/* Recent Awards */}
                {awards?.recent && awards.recent.length > 0 && (
                  <div className={styles.breakdownSection}>
                    <h4 className={styles.subsectionTitle}>Recent Awards (Last 10)</h4>
                    <div className={styles.recentAwardsList}>
                      {awards.recent.slice(0, 10).map((award, index) => (
                        <div key={award._id || index} className={styles.recentAwardItem}>
                          <div className={styles.recentAwardIcon}>
                            <FaStar />
                          </div>
                          <div className={styles.recentAwardContent}>
                            <div className={styles.recentAwardName}>
                              {award.awardName || award.name || 'Award'}
                            </div>
                            <div className={styles.recentAwardMeta}>
                              {award.sport && <span>{award.sport}</span>}
                              {award.awardedAt && (
                                <span>{new Date(award.awardedAt).toLocaleDateString()}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Empty State */}
                {(!awards || awards.total === 0) && (
                  <div className={styles.emptyState}>
                    <FaTrophy className={styles.emptyIcon} />
                    <p>No awards recorded yet</p>
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfileAnalytics;

